import React, { useEffect, useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Clock, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getOfficeHours, saveOfficeHours } from '../lib/api';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Curated list of common IANA timezones for hospitality. Users can request more.
const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Cairo',
  'Asia/Dubai',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Honolulu',
];

const DEFAULTS = {
  enabled: true,
  opensAt: '09:00',
  closesAt: '18:00',
  timezone: 'UTC',
};

const isValidTime = (v) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(v || '');

const minutesOf = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const extractWindow = (scheduleJson) => {
  // Editor saves the same window to all 7 days; pull from Monday as canonical.
  // Fall back to any day if 'mon' is missing.
  if (!scheduleJson || typeof scheduleJson !== 'object') return null;
  const dayEntry = scheduleJson.mon || Object.values(scheduleJson)[0];
  if (!dayEntry || !dayEntry.start || !dayEntry.end) return null;
  return { opensAt: dayEntry.start, closesAt: dayEntry.end };
};

export const OfficeHoursTab = ({ property, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingDoc, setExistingDoc] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!property?.id) return;
      setLoading(true);
      setErrors({});
      try {
        const res = await getOfficeHours(property.id);
        if (cancelled) return;
        const doc = res?.data;
        if (doc) {
          setExistingDoc(doc);
          const win = extractWindow(doc.scheduleJson) || {};
          setForm({
            enabled: doc.afterHoursEnabled !== false,
            opensAt: win.opensAt || DEFAULTS.opensAt,
            closesAt: win.closesAt || DEFAULTS.closesAt,
            timezone: doc.timezone || DEFAULTS.timezone,
          });
        } else {
          setExistingDoc(null);
          setForm({
            ...DEFAULTS,
            timezone: property.timezone || DEFAULTS.timezone,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setExistingDoc(null);
          setForm({ ...DEFAULTS, timezone: property.timezone || DEFAULTS.timezone });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [property?.id]);

  const validate = () => {
    const next = {};
    if (!isValidTime(form.opensAt)) next.opensAt = 'Use 24-hour HH:MM';
    if (!isValidTime(form.closesAt)) next.closesAt = 'Use 24-hour HH:MM';
    if (!next.opensAt && !next.closesAt && minutesOf(form.opensAt) >= minutesOf(form.closesAt)) {
      next.closesAt = 'Closing time must be after opening time';
    }
    if (!form.timezone) next.timezone = 'Pick a timezone';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setSaving(true);
    try {
      const scheduleJson = {};
      DAY_KEYS.forEach((d) => {
        scheduleJson[d] = { start: form.opensAt, end: form.closesAt };
      });
      // Preserve non-V1 fields on the existing doc so a save here doesn't
      // wipe urgent keywords / escalation phone / message template that may
      // have been configured by other tools.
      const payload = {
        propertyId: property.id,
        timezone: form.timezone,
        scheduleJson,
        weekendMode: existingDoc?.weekendMode || 'SAME_AS_WEEKDAY',
        afterHoursEnabled: form.enabled,
        afterHoursMessageTemplate: existingDoc?.afterHoursMessageTemplate
          || 'Thanks for your message! Our team is currently offline but will respond first thing in the morning.',
        urgentKeywords: existingDoc?.urgentKeywords
          || ['emergency', 'urgent', 'safety', 'locked out'],
        escalationPhone: existingDoc?.escalationPhone || '',
      };
      const res = await saveOfficeHours(payload);
      setExistingDoc(res?.data || payload);
      toast.success('Office hours saved');
    } catch (e) {
      toast.error('Could not save office hours');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="office-hours-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const disabled = !isAdmin;

  return (
    <div className="space-y-6" data-testid="office-hours-tab">
      <div className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50/60 p-4">
        <Clock className="h-4 w-4 mt-0.5 text-[#3F617E] shrink-0" />
        <p className="text-[13px] leading-relaxed text-stone-600">
          These hours are used to decide when Maris Desk sends an after-hours acknowledgement to guests
          who submit requests through GuestHub.
        </p>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-stone-200 p-4">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium text-stone-900" htmlFor="auto-ack-switch">
            After-hours auto-acknowledgement
          </Label>
          <p className="text-[12.5px] text-stone-500">
            Send an automatic email to the guest when a request arrives outside office hours.
          </p>
        </div>
        <Switch
          id="auto-ack-switch"
          checked={!!form.enabled}
          onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: !!v }))}
          disabled={disabled}
          data-testid="office-hours-enabled-switch"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="office-hours-timezone">Timezone</Label>
        <Select
          value={form.timezone}
          onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}
          disabled={disabled}
        >
          <SelectTrigger id="office-hours-timezone" data-testid="office-hours-timezone-select">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONE_OPTIONS.map((tz) => (
              <SelectItem key={tz} value={tz} data-testid={`office-hours-tz-${tz}`}>
                {tz}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.timezone && (
          <p className="text-[12px] text-red-600" data-testid="office-hours-tz-error">{errors.timezone}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="office-hours-opens">Opens at</Label>
          <Input
            id="office-hours-opens"
            type="time"
            step="60"
            value={form.opensAt}
            onChange={(e) => setForm((f) => ({ ...f, opensAt: e.target.value }))}
            disabled={disabled}
            data-testid="office-hours-opens-input"
            aria-invalid={!!errors.opensAt}
          />
          {errors.opensAt && (
            <p className="text-[12px] text-red-600" data-testid="office-hours-opens-error">{errors.opensAt}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="office-hours-closes">Closes at</Label>
          <Input
            id="office-hours-closes"
            type="time"
            step="60"
            value={form.closesAt}
            onChange={(e) => setForm((f) => ({ ...f, closesAt: e.target.value }))}
            disabled={disabled}
            data-testid="office-hours-closes-input"
            aria-invalid={!!errors.closesAt}
          />
          {errors.closesAt && (
            <p className="text-[12px] text-red-600" data-testid="office-hours-closes-error">{errors.closesAt}</p>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-[#3F617E] hover:bg-[#345470] text-white"
            data-testid="office-hours-save-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving…' : 'Save office hours'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default OfficeHoursTab;
